/**
 * EHS MIS — Unit Performance Data.
 *
 * Renders the client's "EHS MIS: UNIT PERFORMANCE DATA" workbook as a
 * site-scoped April-March grid, plus CSV / Excel / PDF download.
 *
 * The backend (/api/reports/ehs-mis) owns scoping and authorization: it
 * resolves the site fail-closed and gates on the safety read grant, so this
 * page never decides who may see what. Rows the platform cannot source come
 * back flagged `available: false` with a reason and are rendered as blanks
 * with that reason attached — never as zeros, which would read as "none
 * occurred" rather than "not captured".
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Download, FileText, FileSpreadsheet, FileDown,
  Loader2, RefreshCw, Info, AlertTriangle, Save, Pencil,
  SlidersHorizontal, Plus, Trash2, Eye, EyeOff,
} from 'lucide-react';
import TabNavigation from '../../components/TabNavigation';
import MisViewSwitcher from './MisViewSwitcher';
import { useSite } from '../../context/SiteContext';
import { useAlert } from '../../context/AlertContext';
import {
  fetchEhsMis, downloadEhsMis, saveEhsMisEntries, saveBlob,
  createCustomRow, deleteCustomRow, setHiddenRows,
} from '../../services/ehsMisApi';
import {
  formatNumber, getTenantCountry, getTenantCurrency, getCurrencySymbol,
} from '../../utils/regional';

// Presentational grouping only — the workbook itself is a flat list. Keyed by
// the first row of each section as returned by the backend.
const SECTION_STARTS = {
  roll_employees: 'Manpower & Man-hours',
  major_accident: 'Accidents & Injuries',
  unsafe_act_captured: 'Observations',
  ehs_training_hours: 'Training, Drills & Audits',
  raw_water: 'Environment',
  road_accident: 'Road Safety & Permits',
};

const DOWNLOAD_FORMATS = [
  { id: 'excel', label: 'Excel', Icon: FileSpreadsheet },
  { id: 'pdf', label: 'PDF', Icon: FileText },
  { id: 'csv', label: 'CSV', Icon: FileDown },
];

// REG-08: tenant-locale number formatting. Tenants with no country configured
// keep the legacy en-IN (lakh) grouping so existing India reports are unchanged.
const legacyNumberFmt = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 });

/** The financial year in progress (April-March). */
const currentFyStartYear = () => {
  const now = new Date();
  return now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1;
};

const fyLabel = (year) => `FY ${year}-${String(year + 1).slice(-2)}`;

const formatValue = (value) => {
  if (value === null || value === undefined) return '—';
  return getTenantCountry()
    ? formatNumber(value, { maximumFractionDigits: 2 })
    : legacyNumberFmt.format(value);
};

// What the year-end cell means for a row. Summing a rate, a running total or a
// headcount snapshot would invent a number, so the backend says how each row
// collapses and the column is labelled accordingly.
const AGG_SUFFIX = { sum: '', last: 'YTD', avg: 'avg', none: '' };
const AGG_TITLE = {
  sum: 'Sum of the twelve months',
  last: 'Year-to-date figure at March (a running total, not a sum)',
  avg: 'Average across the months reported (a headcount, not a total)',
  none: 'No meaningful annual figure for this row',
};

const EHSMISReport = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { showError, showSuccess, confirm } = useAlert();
  const { activeSiteId, availableSites, registerSiteSwitchGuard } = useSite();

  const [fy, setFy] = useState(currentFyStartYear());
  const [costPerManDay, setCostPerManDay] = useState(2000);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  // Pending edits to the hand-entered rows: { rowKey: { monthIndex: rawString } }.
  // Held as typed strings so a half-typed "1." doesn't get coerced mid-keystroke;
  // '' means "clear this cell".
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  // "Manage rows" panel state.
  const [managing, setManaging] = useState(false);
  const [savingLayout, setSavingLayout] = useState(false);
  const [newRow, setNewRow] = useState({
    label: '', responsibility: '', unit: '',
    aggregation: 'sum', show_cumulative: false,
  });

  // The site comes from the global selector in the header — there is no
  // per-page override.
  const selectedSiteId = activeSiteId || '';

  // Switching site in the header must discard pending edits. They were typed
  // against the previous site, and saving them afterwards would write one
  // site's figures into another's row.
  useEffect(() => {
    setEdits({});
  }, [activeSiteId]);

  const fyOptions = useMemo(() => {
    const current = currentFyStartYear();
    return Array.from({ length: 6 }, (_, i) => current - i);
  }, []);

  // A site-specific report needs a site. A corporate user viewing "All Sites"
  // has none resolved, so prompt rather than silently reporting across them.
  const needsSiteChoice = !selectedSiteId && availableSites.length > 1;

  const load = useCallback(async (signal) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchEhsMis(
        { siteId: selectedSiteId || undefined, fy, costPerManDay },
        { signal },
      );
      setReport(data);
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 403) {
        setError('You do not have permission to view the EHS MIS report.');
      } else if (status === 400) {
        setError(detail || 'Select a single site to run this report.');
      } else {
        setError(detail || 'Could not load the EHS MIS report.');
      }
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [selectedSiteId, fy, costPerManDay]);

  useEffect(() => {
    if (needsSiteChoice) {
      setReport(null);
      return undefined;
    }
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load, needsSiteChoice]);

  const handleDownload = async (format) => {
    setDownloading(format);
    setMenuOpen(false);
    try {
      const { blob, filename } = await downloadEhsMis({
        siteId: selectedSiteId || undefined, fy, costPerManDay, format,
      });
      saveBlob(blob, filename);
    } catch (err) {
      showError(err?.response?.data?.detail || `Could not download the ${format} report.`);
    } finally {
      setDownloading(null);
    }
  };

  // Drives the one-line hint above the grid telling the user which rows they
  // can fill in (and whether they are allowed to).
  const handEntered = useMemo(
    () => (report?.rows || []).filter((r) => r.editable),
    [report],
  );

  // Hidden rows stay in the payload so the manage panel can offer them back;
  // the grid and every download show only the visible ones.
  const visibleRows = useMemo(
    () => (report?.rows || []).filter((r) => !r.hidden),
    [report],
  );

  const canEdit = Boolean(report?.can_edit);
  const dirtyCount = useMemo(
    () => Object.values(edits).reduce((n, months) => n + Object.keys(months).length, 0),
    [edits],
  );

  // Confirm before the header's site selector discards pending edits. This runs
  // BEFORE the switch, so cancelling keeps both the site and the typed values.
  // The activeSiteId effect above is still the safety net: if the switch does go
  // ahead, the edits are cleared so they can never be saved against a
  // different site.
  useEffect(() => {
    if (dirtyCount === 0) return undefined;
    return registerSiteSwitchGuard(async () => confirm(
      'Discard unsaved values?',
      `You have ${dirtyCount} unsaved cell${dirtyCount === 1 ? '' : 's'} for `
      + 'this site. Switching site will discard them.',
      { confirmLabel: 'Discard', destructive: true },
    ));
  }, [dirtyCount, registerSiteSwitchGuard, confirm]);

  const setCell = (rowKey, monthIndex, raw) => {
    setEdits((prev) => ({
      ...prev,
      [rowKey]: { ...(prev[rowKey] || {}), [monthIndex]: raw },
    }));
  };

  /** The value to show in a cell: a pending edit wins over the saved value. */
  const cellValue = (row, monthIndex) => {
    const pending = edits[row.key]?.[monthIndex];
    if (pending !== undefined) return pending;
    const saved = row.values[monthIndex];
    return saved === null || saved === undefined ? '' : String(saved);
  };

  // Changing site or year reloads the grid, which would drop pending edits —
  // confirm first rather than losing typed values silently.
  // Changing the year reloads the grid, which would drop pending edits — confirm
  // first rather than losing typed values silently. A site change happens in the
  // header, outside this page, so it cannot be intercepted; pending edits are
  // discarded there instead (see the activeSiteId effect above).
  const guardYearChange = async (apply) => {
    if (dirtyCount > 0) {
      const ok = await confirm(
        'Discard unsaved values?',
        `You have ${dirtyCount} unsaved cell${dirtyCount === 1 ? '' : 's'}. `
        + 'Changing the year will discard them.',
        { confirmLabel: 'Discard', destructive: true },
      );
      if (!ok) return;
    }
    setEdits({});
    apply();
  };

  /** Toggle one row's visibility. Sends the whole desired set, not a delta. */
  const toggleRowVisibility = async (rowKey) => {
    const current = new Set(report?.hidden_rows || []);
    if (current.has(rowKey)) current.delete(rowKey);
    else current.add(rowKey);
    setSavingLayout(true);
    try {
      await setHiddenRows(selectedSiteId || undefined, Array.from(current));
      await load();
    } catch (err) {
      showError(err?.response?.data?.detail || 'Could not update row visibility.');
    } finally {
      setSavingLayout(false);
    }
  };

  const handleAddCustomRow = async () => {
    const label = newRow.label.trim();
    if (!label) {
      showError('Give the row a name.');
      return;
    }
    setSavingLayout(true);
    try {
      await createCustomRow(selectedSiteId || undefined, { ...newRow, label });
      setNewRow({
        label: '', responsibility: '', unit: '',
        aggregation: 'sum', show_cumulative: false,
      });
      await load();
      showSuccess(`Added “${label}”.`);
    } catch (err) {
      showError(err?.response?.data?.detail || 'Could not add the row.');
    } finally {
      setSavingLayout(false);
    }
  };

  const handleDeleteCustomRow = async (row) => {
    const ok = await confirm(
      'Remove this row?',
      `“${row.label}” will no longer appear on the report. Any values already `
      + 'entered against it are kept, so re-adding the row does not lose them.',
      { confirmLabel: 'Remove', destructive: true },
    );
    if (!ok) return;
    setSavingLayout(true);
    try {
      await deleteCustomRow(selectedSiteId || undefined, row.id);
      await load();
    } catch (err) {
      showError(err?.response?.data?.detail || 'Could not remove the row.');
    } finally {
      setSavingLayout(false);
    }
  };

  const handleSave = async () => {
    const entries = [];
    const invalid = [];
    Object.entries(edits).forEach(([rowKey, months]) => {
      Object.entries(months).forEach(([monthIndex, raw]) => {
        const trimmed = String(raw).trim();
        if (trimmed === '') {
          entries.push({ row_key: rowKey, month_index: Number(monthIndex), value: null });
          return;
        }
        const num = Number(trimmed);
        if (!Number.isFinite(num)) {
          invalid.push(`${rowKey}[${monthIndex}]`);
          return;
        }
        entries.push({ row_key: rowKey, month_index: Number(monthIndex), value: num });
      });
    });

    if (invalid.length) {
      showError(`These cells are not numbers: ${invalid.join(', ')}`);
      return;
    }
    if (!entries.length) return;

    setSaving(true);
    try {
      await saveEhsMisEntries({ siteId: selectedSiteId || undefined, fy, entries });
      setEdits({});
      // Refetch so the derived accumulative rows pick up the new months.
      await load();
      showSuccess(`Saved ${entries.length} value${entries.length === 1 ? '' : 's'}.`);
    } catch (err) {
      const status = err?.response?.status;
      showError(
        status === 403
          ? 'You do not have permission to enter EHS MIS values.'
          : err?.response?.data?.detail || 'Could not save the entered values.',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />

      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/safety')}
            className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-3"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Safety
          </button>
          {/* Both MIS views live behind one Safety Hub card; this switches
              between them. Guarded, because this grid can hold typed values that
              have not been saved yet. */}
          <MisViewSwitcher
            active="workbook"
            onBeforeLeave={async () => {
              if (dirtyCount === 0) return true;
              return confirm(
                'Discard unsaved values?',
                `You have ${dirtyCount} unsaved cell${dirtyCount === 1 ? '' : 's'}. `
                + 'Leaving this view will discard them.',
                { confirmLabel: 'Discard', destructive: true },
              );
            }}
          />
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">EHS MIS</h1>
              <p className="text-gray-600 mt-1">
                Unit performance data
                {report ? ` — ${report.site_label}, ${report.fy_label}` : ''}
              </p>
              {report && (
                <p className="text-xs text-gray-500 mt-1.5">
                  <span className="font-medium text-gray-600">Emission factors:</span>{' '}
                  {report.factor_set}
                  <span className="mx-2 text-gray-300">·</span>
                  <span className="font-medium text-gray-600">Frequency Rate:</span>{' '}
                  {report.fr_basis}
                </p>
              )}
            </div>

            <div className="relative">
              <button
                onClick={() => setMenuOpen((o) => !o)}
                disabled={!report || Boolean(downloading)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg
                           hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {downloading
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <Download className="h-4 w-4" />}
                Download
              </button>
              {menuOpen && report && (
                <div className="absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                  {DOWNLOAD_FORMATS.map(({ id, label, Icon }) => (
                    <button
                      key={id}
                      onClick={() => handleDownload(id)}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left"
                    >
                      <Icon className="h-4 w-4" />
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap items-end gap-4">
            {/* No site picker here — the site is chosen in the header bar. */}
            <div>
              <label htmlFor="mis-fy" className="block text-xs font-medium text-gray-500 mb-1">
                Financial year
              </label>
              <select
                id="mis-fy"
                value={fy}
                onChange={(e) => {
                  const next = Number(e.target.value);
                  guardYearChange(() => setFy(next));
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                {fyOptions.map((y) => (
                  <option key={y} value={y}>{fyLabel(y)}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="mis-cost" className="block text-xs font-medium text-gray-500 mb-1">
                {/* REG-08: tenant currency symbol; no parenthetical when unset */}
                Cost per man-day lost{getTenantCurrency() ? ` (${getCurrencySymbol(getTenantCurrency())})` : ''}
              </label>
              <input
                id="mis-cost"
                type="number"
                min="0"
                value={costPerManDay}
                onChange={(e) => setCostPerManDay(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-40"
              />
            </div>

            <button
              onClick={() => load()}
              disabled={loading || needsSiteChoice}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg
                         text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>

            {canEdit && (
              <button
                onClick={() => setManaging((o) => !o)}
                className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg
                           text-sm text-gray-700 hover:bg-gray-50"
              >
                <SlidersHorizontal className="h-4 w-4" />
                Manage rows
              </button>
            )}

            {canEdit && (
              <button
                onClick={handleSave}
                disabled={saving || dirtyCount === 0}
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg
                           text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <Save className="h-4 w-4" />}
                {dirtyCount > 0 ? `Save ${dirtyCount} value${dirtyCount === 1 ? '' : 's'}` : 'Save'}
              </button>
            )}
          </div>

          {report && handEntered.length > 0 && (
            <p className="mt-3 text-xs text-gray-500 flex items-start gap-1.5">
              <Pencil className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              {canEdit
                ? `${handEntered.length} rows have no data source in the platform yet and can be `
                  + 'filled in below. Entered values are saved against this site and year, appear '
                  + 'in the download, and are labelled as manual entries.'
                : `${handEntered.length} rows have no data source in the platform yet. Entering `
                  + 'values requires the reports:create permission, which your role does not hold.'}
            </p>
          )}
        </div>

        {needsSiteChoice && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-800">
              This report is per unit. Pick a site in the header bar to run it.
            </p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {loading && !report && (
          <div className="flex items-center justify-center py-20 text-gray-500">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            Building report…
          </div>
        )}

        {/* Manage rows — visibility toggles and site-defined rows */}
        {report && canEdit && managing && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Manage rows</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Applies to this site for every year. Hiding a row only removes it
                  from the report — the value is still calculated, so a hidden
                  man-hours row keeps feeding the Frequency Rate.
                </p>
              </div>
              {savingLayout && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
            </div>

            {/* Add a site-defined row */}
            <div className="border border-gray-200 rounded-lg p-4 mb-5 bg-gray-50/60">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Add a row</h3>
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[220px]">
                  <label htmlFor="cr-label" className="block text-xs text-gray-500 mb-1">
                    Row name
                  </label>
                  <input
                    id="cr-label"
                    value={newRow.label}
                    onChange={(e) => setNewRow({ ...newRow, label: e.target.value })}
                    placeholder="e.g. Safety committee meetings"
                    maxLength={255}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="cr-resp" className="block text-xs text-gray-500 mb-1">
                    Responsibility
                  </label>
                  <input
                    id="cr-resp"
                    value={newRow.responsibility}
                    onChange={(e) => setNewRow({ ...newRow, responsibility: e.target.value })}
                    placeholder="EHS"
                    maxLength={100}
                    className="w-28 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="cr-unit" className="block text-xs text-gray-500 mb-1">
                    Unit
                  </label>
                  <input
                    id="cr-unit"
                    value={newRow.unit}
                    onChange={(e) => setNewRow({ ...newRow, unit: e.target.value })}
                    placeholder="Nos"
                    maxLength={50}
                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="cr-agg" className="block text-xs text-gray-500 mb-1"
                         title="How the twelve months collapse into the Year column.">
                    Year figure
                  </label>
                  <select
                    id="cr-agg"
                    value={newRow.aggregation}
                    onChange={(e) => setNewRow({ ...newRow, aggregation: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="sum">Sum of months</option>
                    <option value="avg">Average of months</option>
                    <option value="last">Final month (YTD)</option>
                    <option value="none">No annual figure</option>
                  </select>
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
                  <input
                    type="checkbox"
                    checked={newRow.show_cumulative}
                    onChange={(e) => setNewRow({ ...newRow, show_cumulative: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  Add an accumulative row
                </label>
                <button
                  onClick={handleAddCustomRow}
                  disabled={savingLayout || !newRow.label.trim()}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white
                             rounded-lg text-sm hover:bg-emerald-700 disabled:opacity-50"
                >
                  <Plus className="h-4 w-4" />
                  Add
                </button>
              </div>
            </div>

            {/* Existing site-defined rows */}
            {report.custom_rows?.length > 0 && (
              <div className="mb-5">
                <h3 className="text-sm font-semibold text-gray-800 mb-2">
                  Your rows ({report.custom_rows.length})
                </h3>
                <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
                  {report.custom_rows.map((c) => (
                    <li key={c.id} className="flex items-center justify-between px-3 py-2">
                      <span className="text-sm text-gray-900">
                        {c.label}
                        <span className="ml-2 text-xs text-gray-400">
                          {[c.responsibility, c.unit, `year: ${c.aggregation}`]
                            .filter(Boolean).join(' · ')}
                          {c.show_cumulative ? ' · with accumulative' : ''}
                        </span>
                      </span>
                      <button
                        onClick={() => handleDeleteCustomRow(c)}
                        disabled={savingLayout}
                        title="Remove this row"
                        className="text-red-500 hover:text-red-700 disabled:opacity-40"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Visibility of every row */}
            <h3 className="text-sm font-semibold text-gray-800 mb-2">
              Rows on the report ({visibleRows.length} of {report.rows.length})
            </h3>
            <div className="border border-gray-200 rounded-lg max-h-80 overflow-y-auto divide-y divide-gray-100">
              {report.rows.map((row) => (
                <div key={row.key}
                     className={`flex items-center justify-between px-3 py-1.5 text-sm ${
                       row.hidden ? 'bg-gray-50 text-gray-400' : 'text-gray-800'
                     }`}>
                  <span className="truncate pr-3">
                    {row.label}
                    {row.unit && <span className="ml-2 text-xs text-gray-400">{row.unit}</span>}
                  </span>
                  <button
                    onClick={() => toggleRowVisibility(row.key)}
                    disabled={savingLayout}
                    title={row.hidden ? 'Show on the report' : 'Hide from the report'}
                    className="flex-shrink-0 text-gray-400 hover:text-gray-700 disabled:opacity-40"
                  >
                    {row.hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Grid */}
        {report && (
          <>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm border-collapse">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="sticky left-0 z-20 bg-gray-50 text-left px-4 py-3 font-semibold
                                     text-gray-700 border-b border-r border-gray-200 min-w-[320px]">
                        Element / Item
                      </th>
                      <th className="text-left px-3 py-3 font-semibold text-gray-700 border-b border-gray-200 whitespace-nowrap">
                        Responsibility
                      </th>
                      <th className="text-left px-3 py-3 font-semibold text-gray-700 border-b border-gray-200">
                        Unit
                      </th>
                      {report.months.map((m) => (
                        <th key={m.key}
                            className="text-right px-3 py-3 font-semibold text-gray-700 border-b border-gray-200 whitespace-nowrap">
                          {m.label}
                        </th>
                      ))}
                      <th title="Sum, year-to-date value or average depending on the row — see the label on each cell."
                          className="text-right px-4 py-3 font-semibold text-gray-700 border-b border-l border-gray-200 bg-gray-100">
                        Year
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((row) => {
                      const section = SECTION_STARTS[row.key];
                      const derived = row.responsibility === 'Auto Calculation';
                      const colSpan = 4 + report.months.length;
                      return (
                        <React.Fragment key={row.key}>
                          {section && (
                            <tr>
                              <td colSpan={colSpan}
                                  className="sticky left-0 bg-slate-100 px-4 py-2 text-xs font-bold
                                             uppercase tracking-wide text-slate-600 border-y border-slate-200">
                                {section}
                              </td>
                            </tr>
                          )}
                          <tr className={`border-b border-gray-100 hover:bg-blue-50/40 ${
                            row.available ? '' : 'bg-gray-50/70'
                          }`}>
                            <th scope="row"
                                className={`sticky left-0 z-10 text-left px-4 py-2 font-normal border-r border-gray-200
                                            ${row.available ? 'bg-white' : 'bg-gray-50'}
                                            ${derived ? 'text-gray-500 italic' : 'text-gray-900'}`}>
                              <span className={row.available ? '' : 'text-gray-400'}>{row.label}</span>
                              {row.source === 'manual' && (
                                <span title={`Entered by hand — ${row.reason}`}
                                      className="ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-medium
                                                 bg-violet-100 text-violet-800 align-middle cursor-help">
                                  manual
                                </span>
                              )}
                              {row.source !== 'manual' && !row.available && (
                                <span title={row.reason}
                                      className="ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-medium
                                                 bg-amber-100 text-amber-800 align-middle cursor-help">
                                  {row.editable ? 'enter below' : 'not captured'}
                                </span>
                              )}
                              {row.available && !row.site_scoped && (
                                <span title="audit_records has no site_id column, so this figure covers the whole tenant."
                                      className="ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-medium
                                                 bg-sky-100 text-sky-800 align-middle cursor-help">
                                  tenant-wide
                                </span>
                              )}
                            </th>
                            <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{row.responsibility}</td>
                            <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{row.unit}</td>
                            {row.values.map((v, i) => (
                              <td key={report.months[i].key}
                                  className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${
                                    derived ? 'text-gray-500' : 'text-gray-900'
                                  } ${row.editable && canEdit ? 'p-1' : ''}`}>
                                {row.editable && canEdit ? (
                                  <input
                                    type="number"
                                    step="any"
                                    inputMode="decimal"
                                    aria-label={`${row.label} — ${report.months[i].label}`}
                                    value={cellValue(row, i)}
                                    onChange={(e) => setCell(row.key, i, e.target.value)}
                                    className={`w-20 px-1.5 py-1 text-right tabular-nums rounded border text-sm
                                                focus:outline-none focus:ring-1 focus:ring-violet-400 ${
                                      edits[row.key]?.[i] !== undefined
                                        ? 'border-violet-400 bg-violet-50'
                                        : 'border-gray-200 bg-white'
                                    }`}
                                  />
                                ) : formatValue(v)}
                              </td>
                            ))}
                            <td title={AGG_TITLE[row.aggregation]}
                                className="px-4 py-2 text-right tabular-nums font-semibold text-gray-900
                                           border-l border-gray-200 bg-gray-50 whitespace-nowrap">
                              {formatValue(row.total)}
                              {row.total !== null && AGG_SUFFIX[row.aggregation] && (
                                <span className="ml-1 text-[10px] font-normal text-gray-400 align-middle">
                                  {AGG_SUFFIX[row.aggregation]}
                                </span>
                              )}
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* The "Entered by hand", "Not captured by the platform" and
                "Notes on this report" panels were removed on request — the page
                is the grid alone, matching the downloaded report. Per-row
                context is still available inline: the `manual` / `enter below`
                / `tenant-wide` badges carry each row's `reason` as a tooltip,
                and the backend still returns `caveats` on the JSON payload for
                anything that needs it. */}
          </>
        )}
      </div>
    </div>
  );
};

export default EHSMISReport;
